import { useMemo, useState } from "react";

import { useInfiniteQuery } from "@tanstack/react-query";

import { getUserIdentities } from "app/components/impersonate-popup/api/getUserIdentities";
import { MIN_USER_SEARCH_TERM_QUERY_LENGTH } from "app/components/impersonate-popup/helpers";
import type { TableData, User } from "app/components/impersonate-popup/types";
import { translate } from "app/filters/i18nFilter";

const IMPERSONATE_LIMIT = 100;

export const useUserSearch = () => {
    const [search, setSearch] = useState("");
    const [searchInField, setSearchInField] = useState("");

    const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
        queryKey: ["users", search, searchInField],
        queryFn: async ({ pageParam = 1, signal }) => {
            if (!search) {
                return [];
            }
            return await getUserIdentities(search, searchInField, pageParam, IMPERSONATE_LIMIT, signal);
        },
        getNextPageParam: (lastPage, allPages) => {
            // If the last page has fewer items than the limit, we've reached the end
            if (lastPage.length < IMPERSONATE_LIMIT) {
                return undefined;
            }
            return allPages.length + 1;
        },
        enabled: !!search && search.length >= MIN_USER_SEARCH_TERM_QUERY_LENGTH,
        initialPageParam: 1,
    });

    // Flatten all pages and transform the data
    const tableData = useMemo(() => {
        if (!data?.pages) {
            return null;
        }

        const allUsers = data.pages.flat();
        const Records = allUsers.map((user: User) => ({
            ...user,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            Role: translate(`Usermanagement.Role.${user.Role}`),
        }));

        // eslint-disable-next-line @typescript-eslint/naming-convention
        return { Records } as TableData;
    }, [data]);

    return {
        search,
        fetchUsers: setSearch,
        setSearchInField,
        loading: isLoading,
        tableData,
        error: error ? "Failed to load user data. Please try again." : null,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    };
};
